import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Save, Eye, Plus, Trash2, ArrowUp, ArrowDown, ExternalLink, Download, FileText, Printer } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Switch } from "@/components/ui/switch";
import { Textarea } from "@/components/ui/textarea";
import { RichTextarea } from "@/components/ui/RichTextarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { getCmsLayout, saveCmsLayoutDraft, publishCmsLayout, CmsLayout } from "@/data/cmsStore";
import { useToast } from "@/components/ui/use-toast";
import { useI18n } from "@/i18n/I18nProvider";
import { useNewsletterStore, NewsletterSubscriber } from "@/data/newsletterStore";
import { isSoftLaunchEnabled, setSoftLaunchEnabled } from "@/data/softLaunchStore";
import { useSearchParams } from "react-router-dom";

interface FooterLinkConfig {
  labelFr: string;
  labelAr: string;
  labelZgh?: string;
  url: string;
}

interface SocialLink {
  platform: string;
  icon: string;
  url: string;
  visible: boolean;
}

interface ContactInfo {
  type: string;
  icon: string;
  value: string;
  url: string;
  visible: boolean;
}

const defaultLinks: FooterLinkConfig[] = [
  { labelFr: "Inscription électorale", labelAr: "التسجيل الانتخابي", labelZgh: "ⴰⵙⵖⵍ ⴰⵏⵙⵜⴰⵢ", url: "/ressources/inscription" },
  { labelFr: "Guide du candidat", labelAr: "دليل المرشح", labelZgh: "ⴰⵎⵔⵙⵉ ⵏ ⵓⵎⵙⵓⵜⵔ", url: "/ressources/candidature" },
  { labelFr: "Partis politiques", labelAr: "الأحزاب السياسية", labelZgh: "ⵉⵎⵓⵔⴰⵏ ⵉⵙⵔⵜⴰⵏⵏ", url: "/partis" },
  { labelFr: "Actions citoyennes", labelAr: "الأعمال المواطنة", labelZgh: "ⵜⵉⴳⴰⵡⵉⵏ ⵏ ⵜⵎⵓⵔⵜ", url: "/actions" },
  { labelFr: "Observateur Électoral", labelAr: "مراقب انتخابي", labelZgh: "ⴰⵎⴰⵏⴰⵢ ⴰⵏⵙⵜⴰⵢ", url: "/observateur" }
];

const defaultSocials: SocialLink[] = [
  { platform: "Facebook", icon: "facebook", url: "#", visible: true },
  { platform: "Instagram", icon: "instagram", url: "#", visible: true },
  { platform: "TikTok", icon: "tiktok", url: "#", visible: true },
  { platform: "LinkedIn", icon: "linkedin", url: "#", visible: true }
];

const defaultContacts: ContactInfo[] = [
  { type: "Email", icon: "mail", value: "mycmchub@gmail.com", url: "mailto:mycmchub@gmail.com", visible: true },
  { type: "Location", icon: "map-pin", value: "Rabat, Maroc", url: "", visible: true }
];

const defaultContent = {
  cta_title_fr: "Prêt à participer à la construction du Maroc de demain ?",
  cta_btn1_fr: "Je veux m'engager",
  cta_btn2_fr: "Je veux observer",
  cta_title_ar: "مستعد للمشاركة في بناء مغرب الغد؟",
  cta_btn1_ar: "أريد المشاركة",
  cta_btn2_ar: "أريد أن ألاحظ",
  cta_title_zgh: "ⵉⵔⵎⴰⵙⵏ ⴰⴷ ⵜⵜⴽⴽⵉⵜ ⴳ ⵓⴱⵏⵉ ⵏ ⵍⵎⵖⵔⵉⴱ ⵏ ⵓⵣⴽⴽⴰ?",
  cta_btn1_zgh: "ⵔⵉⵖ ⴰⴷ ⴰⵡⵙⵖ",
  cta_btn2_zgh: "ⵔⵉⵖ ⴰⴷ ⵜⵜⴽⴽⵉⵖ",
  desc_fr: "Kayn'in, L'espace citoyen nouvelle génération.",
  desc_ar: "فضاءك المواطن للديمقراطية التشاركية، الجيل الجديد.",
  desc_zgh: "ⴰⵙⴰⵢⵔⴰⵔ ⵏⵏⴽ ⴰⵏⴰⵎⵓⵔ ⵏ ⵜⵎⴰⴳⵔⴰⵡⵜ ⵜⴰⵎⴷⵔⴰⵡⵜ, ⵜⴰⵙⵓⵜ ⵜⴰⵎⴰⵢⵏⵓⵜ."
};

const AdminFooterPage = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const { isRtl } = useI18n();
  const { toast } = useToast();
  
  const [softLaunchEnabled, setSoftLaunchEnabledState] = useState(isSoftLaunchEnabled());
  useEffect(() => {
    setSoftLaunchEnabled(softLaunchEnabled);
  }, [softLaunchEnabled]);

  const [layout, setLayout] = useState<CmsLayout[]>([]);
  const [links, setLinks] = useState<FooterLinkConfig[]>([]);
  const [socials, setSocials] = useState<SocialLink[]>([]);
  const [contacts, setContacts] = useState<ContactInfo[]>([]);
  const [footerContent, setFooterContent] = useState<Record<string, string>>(defaultContent);
  const [legalContent, setLegalContent] = useState<Record<string, string>>({});
  const { subscribers, isLoaded, fetchSubscribers } = useNewsletterStore();
  const [activeLegalTab, setActiveLegalTab] = useState("mentions");

  useEffect(() => {
    loadDraft();
    fetchSubscribers();
  }, [fetchSubscribers]);

  const loadDraft = () => {
    const draft = getCmsLayout("global", true);
    setLayout(draft);
    
    let loadedLinks = [...defaultLinks];
    let loadedContent = { ...defaultContent };
    let loadedSocials: SocialLink[] = [...defaultSocials];
    let loadedContacts: ContactInfo[] = [...defaultContacts];
    
    const footerSection = draft.find(s => s.id === "footer");
    if (footerSection && footerSection.content) {
      const { links: savedLinks, socials: savedSocials, contacts: savedContacts, ...restContent } = footerSection.content;
      
      if (savedLinks) {
        try {
          const parsed = JSON.parse(savedLinks);
          if (parsed && Array.isArray(parsed) && parsed.length > 0) loadedLinks = parsed;
        } catch (e) {}
      }
      if (savedSocials) {
        try {
          const parsed = JSON.parse(savedSocials);
          if (parsed && Array.isArray(parsed)) loadedSocials = parsed;
        } catch (e) {}
      }
      if (savedContacts) {
        try {
          const parsed = JSON.parse(savedContacts);
          if (parsed && Array.isArray(parsed)) loadedContacts = parsed;
        } catch (e) {}
      }
      
      loadedContent = { ...defaultContent, ...restContent };
    }
    setLinks(loadedLinks);
    setSocials(loadedSocials);
    setContacts(loadedContacts);
    setFooterContent(loadedContent);

    const legalSection = draft.find(s => s.id === "legal_pages");
    if (legalSection && legalSection.content) {
      setLegalContent(legalSection.content);
    }
  };

  const handleSaveDraft = () => {
    let newLayout = [...layout];
    
    // Save Footer
    let footerExists = false;
    newLayout = newLayout.map(s => {
      if (s.id === "footer") {
        footerExists = true;
        return { ...s, content: { 
          ...footerContent, 
          links: JSON.stringify(links),
          socials: JSON.stringify(socials),
          contacts: JSON.stringify(contacts)
        } };
      }
      return s;
    });

    if (!footerExists) {
      newLayout.push({
        id: "footer",
        type: "FooterData",
        visible: true,
        content: { 
          ...footerContent, 
          links: JSON.stringify(links),
          socials: JSON.stringify(socials),
          contacts: JSON.stringify(contacts)
        }
      });
    }

    // Save Legal Pages
    let legalExists = false;
    newLayout = newLayout.map(s => {
      if (s.id === "legal_pages") {
        legalExists = true;
        return { ...s, content: legalContent };
      }
      return s;
    });

    if (!legalExists) {
      newLayout.push({
        id: "legal_pages",
        type: "LegalPagesData",
        visible: true,
        content: legalContent
      });
    }
    
    setLayout(newLayout);
    saveCmsLayoutDraft("global", newLayout);
    
    toast({
      title: "Brouillon sauvegardé",
      description: "Regardez le pied de page pour voir les changements en direct.",
    });
  };

  const handlePublish = () => {
    handleSaveDraft();
    const draft = getCmsLayout("global", true);
    publishCmsLayout("global", draft);
    
    toast({
      title: "Modifications publiées",
      description: "La nouvelle structure est désormais en ligne.",
      variant: "default"
    });
  };

  const addLink = () => setLinks([...links, { labelFr: "Nouveau Lien", labelAr: "رابط جديد", labelZgh: "ⴰⵣⴷⴰⵢ ⴰⵎⴰⵢⵏⵓ", url: "/" }]);
  const updateLink = (idx: number, key: keyof FooterLinkConfig, val: string) => {
    const updated = [...links];
    updated[idx][key] = val;
    setLinks(updated);
  };
  const removeLink = (idx: number) => setLinks(links.filter((_, i) => i !== idx));
  const moveLink = (idx: number, direction: -1 | 1) => {
    const newIdx = idx + direction;
    if (newIdx < 0 || newIdx >= links.length) return;
    const newLinks = [...links];
    const temp = newLinks[idx];
    newLinks[idx] = newLinks[newIdx];
    newLinks[newIdx] = temp;
    setLinks(newLinks);
  };

  const updateContent = (key: string, val: string) => setFooterContent(prev => ({ ...prev, [key]: val }));
  const updateLegalContent = (key: string, val: string) => setLegalContent(prev => ({ ...prev, [key]: val }));

  // Handlers for Socials
  const addSocial = () => setSocials([...socials, { platform: "Nouveau", icon: "link", url: "#", visible: true }]);
  const updateSocial = (idx: number, key: keyof SocialLink, val: any) => {
    const updated = [...socials];
    (updated[idx] as any)[key] = val;
    setSocials(updated);
  };
  const removeSocial = (idx: number) => setSocials(socials.filter((_, i) => i !== idx));

  const exportCSV = () => {
    const csvContent = "data:text/csv;charset=utf-8," 
      + "Email,Date\n"
      + subscribers.map(s => `${s.email},${new Date(s.date).toLocaleString()}`).join("\n");
    const encodedUri = encodeURI(csvContent);
    const link = document.createElement("a");
    link.setAttribute("href", encodedUri);
    link.setAttribute("download", "abonnes_newsletter.csv");
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const exportWord = () => {
    const header = "<html xmlns:o='urn:schemas-microsoft-com:office:office' xmlns:w='urn:schemas-microsoft-com:office:word' xmlns='http://www.w3.org/TR/REC-html40'><head><meta charset='utf-8'><title>Abonnés Newsletter</title></head><body>";
    const footer = "</body></html>";
    const sourceHTML = header + "<h1>Liste des Abonnés Newsletter</h1><table border='1'><tr><th>Email</th><th>Date d'inscription</th></tr>" + subscribers.map(s => `<tr><td>${s.email}</td><td>${new Date(s.date).toLocaleString()}</td></tr>`).join("") + "</table>" + footer;
    const source = 'data:application/vnd.ms-word;charset=utf-8,' + encodeURIComponent(sourceHTML);
    const fileDownload = document.createElement("a");
    document.body.appendChild(fileDownload);
    fileDownload.href = source;
    fileDownload.download = 'abonnes_newsletter.doc';
    fileDownload.click();
    document.body.removeChild(fileDownload);
  };

  const exportPDF = () => {
    const printWindow = window.open('', '_blank');
    if (!printWindow) return;
    const html = `
      <html>
        <head>
          <title>Abonnés Newsletter</title>
          <style>
            body { font-family: sans-serif; padding: 20px; }
            table { width: 100%; border-collapse: collapse; margin-top: 20px; }
            th, td { border: 1px solid #ddd; padding: 8px; text-align: left; }
            th { background-color: #f2f2f2; }
            @media print {
              button { display: none; }
            }
          </style>
        </head>
        <body>
          <h1>Liste des Abonnés Newsletter (${subscribers.length})</h1>
          <button onclick="window.print()" style="padding: 10px 20px; background: #000; color: #fff; border: none; cursor: pointer; margin-bottom: 20px;">Imprimer / Sauvegarder en PDF</button>
          <table>
            <tr><th>Email</th><th>Date d'inscription</th></tr>
            ${subscribers.map(s => `<tr><td>${s.email}</td><td>${new Date(s.date).toLocaleString()}</td></tr>`).join("")}
          </table>
        </body>
      </html>
    `;
    printWindow.document.write(html);
    printWindow.document.close();
  };

  return (
    <div className="space-y-6" dir={isRtl ? "rtl" : "ltr"}>
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4 border-b pb-6">
        <div>
          <h1 className="text-3xl font-display font-bold">Gestion du Footer & Légales</h1>
          <p className="text-muted-foreground">Personnalisez les textes, les liens, les réseaux et les pages légales.</p>
        </div>
        <div className="flex gap-3">
          <Button variant="outline" onClick={handleSaveDraft} className="gap-2">
            <Save className="w-4 h-4" /> Sauvegarder Brouillon
          </Button>
          <Button onClick={handlePublish} className="gap-2 shadow-[var(--shadow-elevated)]">
            <Eye className="w-4 h-4" /> Publier En Ligne
          </Button>
        </div>
      </div>

        <Card className="mb-6 border-primary/20">
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-lg">Mode Soft Launch</CardTitle>
            <Switch checked={softLaunchEnabled} onCheckedChange={setSoftLaunchEnabledState} />
          </CardHeader>
        </Card>

      <div className="grid lg:grid-cols-12 gap-8">
        <div className="lg:col-span-8">
          <Tabs value={searchParams.get("tab") || "general"} onValueChange={(v) => setSearchParams({ tab: v })} className="w-full">
            <TabsList className="mb-6 w-full justify-start h-auto p-1 bg-muted/50 overflow-x-auto">
              <TabsTrigger value="general" className="px-6 py-2.5">Général</TabsTrigger>
              <TabsTrigger value="socials" className="px-6 py-2.5">Contact & Réseaux</TabsTrigger>
              <TabsTrigger value="legal" className="px-6 py-2.5">Pages Légales</TabsTrigger>
              <TabsTrigger value="newsletter" className="px-6 py-2.5">Newsletter</TabsTrigger>
              <TabsTrigger value="subscribers" className="px-6 py-2.5">Abonnés</TabsTrigger>
            </TabsList>

            {/* ONGLET GENERAL */}
            <TabsContent value="general" className="space-y-6 mt-0">
              <Card className="border-primary/20 shadow-sm">
                <CardHeader className="pb-4">
                  <CardTitle className="text-lg">Description de la Marque</CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="space-y-1">
                    <Label className="text-xs">Description (Français)</Label>
                    <Input value={footerContent.desc_fr || ""} onChange={e => updateContent("desc_fr", e.target.value)} />
                  </div>
                  <div className="space-y-1">
                    <Label className="text-xs">Description (Arabe)</Label>
                    <Input dir="rtl" value={footerContent.desc_ar || ""} onChange={e => updateContent("desc_ar", e.target.value)} />
                  </div>
                  <div className="space-y-1">
                    <Label className="text-xs">Description (Amazigh)</Label>
                    <Input dir="ltr" value={footerContent.desc_zgh || ""} onChange={e => updateContent("desc_zgh", e.target.value)} />
                  </div>
                </CardContent>
              </Card>

              <Card className="border-primary/20 shadow-sm">
                <CardHeader className="pb-4">
                  <CardTitle className="text-lg">Pre-Footer Call to Action (CTA)</CardTitle>
                </CardHeader>
                <CardContent className="space-y-6">
                  {['fr', 'ar', 'zgh'].map(l => (
                    <div key={l} className="grid grid-cols-1 md:grid-cols-3 gap-4">
                      <div className="space-y-1">
                        <Label className="text-xs">Titre ({l})</Label>
                        <Input dir={l === 'ar' ? 'rtl' : 'ltr'} value={footerContent[`cta_title_${l}`] || ""} onChange={e => updateContent(`cta_title_${l}`, e.target.value)} />
                      </div>
                      <div className="space-y-1">
                        <Label className="text-xs">Bouton 1 ({l})</Label>
                        <Input dir={l === 'ar' ? 'rtl' : 'ltr'} value={footerContent[`cta_btn1_${l}`] || ""} onChange={e => updateContent(`cta_btn1_${l}`, e.target.value)} />
                      </div>
                      <div className="space-y-1">
                        <Label className="text-xs">Bouton 2 ({l})</Label>
                        <Input dir={l === 'ar' ? 'rtl' : 'ltr'} value={footerContent[`cta_btn2_${l}`] || ""} onChange={e => updateContent(`cta_btn2_${l}`, e.target.value)} />
                      </div>
                    </div>
                  ))}
                </CardContent>
              </Card>

              <Card>
                <CardHeader className="flex flex-row items-center justify-between pb-2">
                  <CardTitle className="text-lg">Liens Utiles ({links.length})</CardTitle>
                  <Button size="sm" onClick={addLink} className="gap-1"><Plus className="w-4 h-4" /> Ajouter</Button>
                </CardHeader>
                <CardContent className="pt-4 space-y-4">
                  {links.map((link, idx) => (
                    <div key={idx} className="p-4 bg-muted/40 border rounded-xl flex gap-4 relative group">
                      <div className="flex flex-col gap-1 items-center justify-center border-r border-border/50 pr-4">
                        <Button variant="ghost" size="icon" className="h-6 w-6" onClick={() => moveLink(idx, -1)} disabled={idx === 0}><ArrowUp className="w-4 h-4" /></Button>
                        <Button variant="ghost" size="icon" className="h-6 w-6" onClick={() => moveLink(idx, 1)} disabled={idx === links.length - 1}><ArrowDown className="w-4 h-4" /></Button>
                      </div>
                      <div className="flex-1 grid grid-cols-2 gap-4">
                        <div className="space-y-1"><Label className="text-xs">Français</Label><Input value={link.labelFr} onChange={e => updateLink(idx, "labelFr", e.target.value)} /></div>
                        <div className="space-y-1"><Label className="text-xs">Arabe</Label><Input dir="rtl" value={link.labelAr} onChange={e => updateLink(idx, "labelAr", e.target.value)} /></div>
                        <div className="space-y-1"><Label className="text-xs">Amazigh</Label><Input value={link.labelZgh || ""} onChange={e => updateLink(idx, "labelZgh", e.target.value)} /></div>
                        <div className="space-y-1"><Label className="text-xs">URL</Label><Input value={link.url} onChange={e => updateLink(idx, "url", e.target.value)} /></div>
                      </div>
                      <Button variant="destructive" size="icon" className="h-8 w-8 absolute -top-3 -right-3 rounded-full" onClick={() => removeLink(idx)}><Trash2 className="w-4 h-4" /></Button>
                    </div>
                  ))}
                </CardContent>
              </Card>
            </TabsContent>

            {/* ONGLET CONTACT & RESEAUX */}
            <TabsContent value="socials" className="space-y-6 mt-0">
              <Card>
                <CardHeader className="flex flex-row items-center justify-between pb-2">
                  <CardTitle className="text-lg">Réseaux Sociaux</CardTitle>
                  <Button size="sm" onClick={addSocial} className="gap-1"><Plus className="w-4 h-4" /> Ajouter</Button>
                </CardHeader>
                <CardContent className="pt-4 space-y-4">
                  {socials.map((soc, idx) => (
                    <div key={idx} className="p-4 bg-muted/40 border rounded-xl flex items-center gap-4 relative">
                      <div className="flex-1 grid grid-cols-1 md:grid-cols-4 gap-4 items-center">
                        <div className="space-y-1">
                          <Label className="text-xs">Plateforme</Label>
                          <Input value={soc.platform} onChange={e => updateSocial(idx, "platform", e.target.value)} placeholder="ex: YouTube" />
                        </div>
                        <div className="space-y-1">
                          <Label className="text-xs">Icône (Nom lucide)</Label>
                          <Input value={soc.icon} onChange={e => updateSocial(idx, "icon", e.target.value)} placeholder="ex: youtube" />
                        </div>
                        <div className="space-y-1 col-span-2">
                          <Label className="text-xs">Lien URL</Label>
                          <Input value={soc.url} onChange={e => updateSocial(idx, "url", e.target.value)} placeholder="https://..." />
                        </div>
                      </div>
                      <div className="flex items-center gap-3 pr-8">
                        <Label className="text-xs cursor-pointer">Visible</Label>
                        <Switch checked={soc.visible} onCheckedChange={(v) => updateSocial(idx, "visible", v)} />
                      </div>
                      <Button variant="ghost" size="icon" className="text-destructive absolute right-2 top-2" onClick={() => removeSocial(idx)}><Trash2 className="w-4 h-4" /></Button>
                    </div>
                  ))}
                </CardContent>
              </Card>
            </TabsContent>

            {/* ONGLET PAGES LEGALES */}
            <TabsContent value="legal" className="space-y-6 mt-0">
              <Card>
                <CardHeader>
                  <CardTitle className="text-lg">Gestion des Composantes Légales</CardTitle>
                  <CardDescription>
                    Gérez les trois sections de la page unifiée "Mentions Légales".
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="mb-6 p-4 border rounded-xl bg-background/50 space-y-4">
                    <Label className="text-sm font-bold block mb-2">Mode d'affichage dans le pied de page</Label>
                    <Select 
                      value={legalContent.display_mode || "unified"} 
                      onValueChange={v => updateLegalContent("display_mode", v)}
                    >
                      <SelectTrigger className="w-full">
                        <SelectValue placeholder="Choisir le mode d'affichage" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="unified">Un seul lien (Conditions et mentions légales) - Affichage unifié</SelectItem>
                        <SelectItem value="separated">Trois liens distincts - Affichage classique séparé</SelectItem>
                      </SelectContent>
                    </Select>
                    <p className="text-xs text-muted-foreground">
                      Ce paramètre contrôle l'apparence des liens légaux tout en bas du site.
                    </p>
                  </div>

                  <div className="mb-6">
                    <Select value={activeLegalTab} onValueChange={setActiveLegalTab}>
                      <SelectTrigger className="w-full md:w-[300px]">
                        <SelectValue placeholder="Choisir une page" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="mentions">Mentions Légales</SelectItem>
                        <SelectItem value="conditions">Conditions d'Utilisation</SelectItem>
                        <SelectItem value="politique">Politique de Confidentialité</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="space-y-6 border p-6 rounded-xl bg-muted/20">
                    <div className="flex items-center justify-between">
                      <h3 className="font-bold text-lg capitalize">{activeLegalTab.replace('_', ' ')}</h3>
                      <div className="flex items-center gap-2 bg-background px-3 py-2 rounded-lg border">
                        <Label htmlFor={`visible-${activeLegalTab}`} className="cursor-pointer text-sm font-bold">Activer cette section :</Label>
                        <Switch 
                          id={`visible-${activeLegalTab}`}
                          checked={legalContent[`${activeLegalTab}_visible`] !== "false"} 
                          onCheckedChange={(v) => updateLegalContent(`${activeLegalTab}_visible`, v ? "true" : "false")} 
                        />
                      </div>
                    </div>

                    {legalContent[`${activeLegalTab}_visible`] !== "false" ? (
                      <>
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                          <div className="space-y-1">
                            <Label>Titre (Fr)</Label>
                            <Input value={legalContent[`${activeLegalTab}_title_fr`] || ""} onChange={e => updateLegalContent(`${activeLegalTab}_title_fr`, e.target.value)} />
                          </div>
                          <div className="space-y-1">
                            <Label>Titre (Ar)</Label>
                            <Input dir="rtl" value={legalContent[`${activeLegalTab}_title_ar`] || ""} onChange={e => updateLegalContent(`${activeLegalTab}_title_ar`, e.target.value)} />
                          </div>
                          <div className="space-y-1">
                            <Label>Titre (Amz)</Label>
                            <Input dir="ltr" value={legalContent[`${activeLegalTab}_title_zgh`] || ""} onChange={e => updateLegalContent(`${activeLegalTab}_title_zgh`, e.target.value)} />
                          </div>
                        </div>

                        <div className="space-y-6">
                          <div className="space-y-2">
                            <Label>Contenu (Français) - HTML accepté</Label>
                            <RichTextarea 
                              className="font-mono text-sm" 
                              placeholder="<h2>Titre de section</h2><p>Paragraphe...</p>"
                              value={legalContent[`${activeLegalTab}_content_fr`] || ""}
                              onChange={e => updateLegalContent(`${activeLegalTab}_content_fr`, e.target.value)}
                              rows={10}
                            />
                          </div>
                          <div className="space-y-2 border-t pt-4">
                            <Label>Contenu (Arabe) - HTML accepté</Label>
                            <RichTextarea 
                              dir="rtl"
                              className="font-mono text-sm text-right" 
                              placeholder="<h2>عنوان القسم</h2><p>فقرة...</p>"
                              value={legalContent[`${activeLegalTab}_content_ar`] || ""}
                              onChange={e => updateLegalContent(`${activeLegalTab}_content_ar`, e.target.value)}
                              rows={10}
                            />
                          </div>
                          <p className="text-xs text-muted-foreground mt-2">Utilisez la barre d'outils pour formater votre texte, ajouter des liens ou des images.</p>
                        </div>
                      </>
                    ) : (
                      <div className="p-8 text-center border border-dashed rounded-lg text-muted-foreground">
                        Cette section est actuellement masquée et n'apparaîtra pas sur la page unifiée.
                      </div>
                    )}
                  </div>
                </CardContent>
              </Card>
            </TabsContent>

            {/* ONGLET NEWSLETTER */}
            <TabsContent value="newsletter" className="space-y-6 mt-0">
              <Card>
                <CardHeader>
                  <CardTitle className="text-lg">Configuration de la Newsletter</CardTitle>
                  <CardDescription>Personnalisez le texte d'accroche et gérez l'affichage du formulaire.</CardDescription>
                </CardHeader>
                <CardContent className="space-y-6">
                  <div className="flex items-center justify-between p-4 bg-muted/20 border rounded-xl">
                    <div>
                      <Label className="text-sm font-bold block mb-1">Afficher le bloc Newsletter</Label>
                      <p className="text-xs text-muted-foreground">Si désactivé, le formulaire de newsletter disparaîtra du pied de page.</p>
                    </div>
                    <Switch 
                      checked={footerContent.newsletter_visible !== "false"} 
                      onCheckedChange={v => updateContent("newsletter_visible", v ? "true" : "false")} 
                    />
                  </div>
                  
                  <div className="space-y-4">
                    <Label className="text-sm font-bold">Texte d'accroche</Label>
                    <div className="space-y-1">
                      <Label className="text-xs">Français</Label>
                      <Input value={footerContent.newsletter_text_fr || ""} onChange={e => updateContent("newsletter_text_fr", e.target.value)} />
                    </div>
                    <div className="space-y-1">
                      <Label className="text-xs">Arabe</Label>
                      <Input dir="rtl" value={footerContent.newsletter_text_ar || ""} onChange={e => updateContent("newsletter_text_ar", e.target.value)} />
                    </div>
                    <div className="space-y-1">
                      <Label className="text-xs">Amazigh</Label>
                      <Input dir="ltr" value={footerContent.newsletter_text_zgh || ""} onChange={e => updateContent("newsletter_text_zgh", e.target.value)} />
                    </div>
                  </div>
                </CardContent>
              </Card>
            </TabsContent>

            {/* ONGLET ABONNES */}
            <TabsContent value="subscribers" className="space-y-6 mt-0">
              <Card>
                <CardHeader className="flex flex-col md:flex-row items-start md:items-center justify-between gap-4">
                  <div>
                    <CardTitle className="text-lg">Liste des Abonnés ({subscribers.length})</CardTitle>
                    <CardDescription>Consultez et exportez les emails collectés via le formulaire du footer.</CardDescription>
                  </div>
                  <div className="flex flex-wrap gap-2">
                    <Button variant="outline" size="sm" onClick={exportCSV} className="gap-2" disabled={subscribers.length === 0}>
                      <Download className="w-4 h-4" /> CSV (Excel)
                    </Button>
                    <Button variant="outline" size="sm" onClick={exportWord} className="gap-2" disabled={subscribers.length === 0}>
                      <FileText className="w-4 h-4" /> Word
                    </Button>
                    <Button variant="outline" size="sm" onClick={exportPDF} className="gap-2" disabled={subscribers.length === 0}>
                      <Printer className="w-4 h-4" /> PDF
                    </Button>
                  </div>
                </CardHeader>
                <CardContent>
                  {!isLoaded ? (
                    <div className="p-12 text-center border border-dashed rounded-lg text-muted-foreground">
                      Chargement des abonnés...
                    </div>
                  ) : subscribers.length === 0 ? (
                    <div className="p-12 text-center border border-dashed rounded-lg text-muted-foreground">
                      Aucun abonné pour le moment.
                    </div>
                  ) : (
                    <div className="border rounded-lg overflow-hidden">
                      <table className="w-full text-sm text-left">
                        <thead className="text-xs text-muted-foreground bg-muted/50">
                          <tr>
                            <th className="px-4 py-3 font-medium">Email</th>
                            <th className="px-4 py-3 font-medium">Date d'inscription</th>
                          </tr>
                        </thead>
                        <tbody className="divide-y">
                          {subscribers.map(sub => (
                            <tr key={sub.id} className="bg-card hover:bg-muted/20 transition-colors">
                              <td className="px-4 py-3 font-medium">{sub.email}</td>
                              <td className="px-4 py-3 text-muted-foreground">{new Date(sub.date).toLocaleString()}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </CardContent>
              </Card>
            </TabsContent>
          </Tabs>
        </div>
        
        {/* RIGHT COLUMN (Preview Helper) */}
        <div className="lg:col-span-4">
          <Card className="bg-primary/5 border-primary/20 sticky top-24">
            <CardHeader>
              <CardTitle className="text-primary flex items-center gap-2">
                <Eye className="w-5 h-5" /> Prévisualisation Live
              </CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-sm text-muted-foreground mb-4">
                Pas besoin d'un cadre de simulation compliqué !
              </p>
              <p className="text-sm">
                Dès que vous appuyez sur <strong className="text-foreground">"Sauvegarder Brouillon"</strong>, 
                vous verrez <strong className="text-accent text-lg">le pied de page</strong> tout en bas de votre écran se mettre à jour instantanément.
              </p>
              <div className="mt-6 p-4 bg-background rounded-lg border text-xs text-center text-muted-foreground">
                Faites défiler tout en bas pour voir vos changements. Pour voir la page Mentions Légales unifiée, vous pouvez cliquer sur le lien dans le footer après sauvegarde.
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default AdminFooterPage;
